"use client";

/**
 * External Slot Picker Modal
 * Lightweight modal for external activities with multiple time slots.
 * Shows clickable slots that open external booking links in a new tab.
 */

import type React from "react";
import { useMemo } from "react";
import { ExternalLink } from "lucide-react";
import FetchModal from "@/fetch-ui/FetchModal";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { ActivityRow, ActivityTimeSlotRow } from "../../_logic/types/db-types";
import { formatSlotLabel } from "../../_logic/utils/time-slot-utils";
import { usePublicViewContext } from "./PublicViewContext";

interface ExternalSlotPickerModalProps {
  activity: ActivityRow;
  timeSlots: ActivityTimeSlotRow[];
  primaryColor: string;
  onClose: () => void;
}

/**
 * Modal showing time slot options for external activities.
 * Each slot links to its own external booking URL.
 */
export function ExternalSlotPickerModal({
  activity,
  timeSlots,
  primaryColor,
  onClose,
}: ExternalSlotPickerModalProps): React.ReactElement {
  // Hooks
  const { timeZone } = usePublicViewContext();

  // Derived values
  const slotsWithLinks = useMemo(() => timeSlots.filter((s) => s.booking_link), [timeSlots]);
  const buttonTextColor = getButtonTextColor(primaryColor);

  // Render
  return (
    <FetchModal
      isOpen
      onClose={onClose}
      title={activity.title}
      subtitle={activity.meeting_location ?? undefined}
      size="auto"
      className="m-0 h-dvh max-h-dvh w-screen rounded-none p-4 sm:my-8 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-md sm:rounded-3xl sm:p-6"
      contentClassName="overflow-y-auto overscroll-contain px-1"
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-600">Select a time slot to book:</p>

        {slotsWithLinks.map((slot) => (
          <a
            key={slot.id}
            href={slot.booking_link!}
            target="_blank"
            rel="noopener noreferrer"
            className="flex touch-manipulation items-center justify-between gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-gray-900">{formatSlotLabel(slot, timeZone)}</span>
              {slot.capacity !== null && (
                <span className="text-xs font-medium text-gray-500">Capacity: {slot.capacity}</span>
              )}
            </div>
            <div
              className="flex shrink-0 items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
              aria-hidden="true"
            >
              Book Now
              <ExternalLink className="h-3 w-3" />
            </div>
            <span className="sr-only">Opens in new tab</span>
          </a>
        ))}

        {slotsWithLinks.length === 0 && (
          <p className="text-sm font-medium text-gray-400">No booking links available for this activity.</p>
        )}
      </div>
    </FetchModal>
  );
}
